import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { useCaseActivitySheriffFileMutation } from "@/features/case-evaluation/api/queries/acitivities-query";
import type { CaseActivity } from "@/features/case-evaluation/types/activities";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { IconEye, IconFile } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../core/buttons/button";
import DragDropFile from "../core/forms/drag-drop-file";
import { useModalControl } from "../core/modals/use-modal-controller";
import Tooltip from "../core/tooltip";

export default function CaseActivityAddFileModal({
	caseRecord,
	activity,
}: {
	caseRecord: CaseRecord;
	activity: CaseActivity;
}) {
	const { closeModal } = useModalControl();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [files, setFiles] = useState<File[]>([]);

	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const caseActivitiesSheriffMutation = useCaseActivitySheriffFileMutation(caseRecord.id);

	const form = useForm({
		defaultValues: {
			id: activity.id,
			case_id: caseRecord.id,
			attachment: null,
		},
	});

	const handleSubmit = async (values: any) => {
		await caseActivitiesSheriffMutation.mutateAsync(values);
		closeModal();
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const objectUrl = URL.createObjectURL(files[0]);
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles);
				setMultipleFiles(newFiles);
				return newFiles;
			});
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full overflow-hidden">
					<div className="flex flex-col w-full gap-4 p-4">
						<div className="col-span-12">
							<span className="text-lg font-semibold text-foreground-dark">Sheriff's Report</span>
						</div>
						<div className="col-span-12 flex w-full">
							<FormField
								control={form.control}
								name="attachment"
								render={() => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Upload Files</FormLabel>
										</div>
										<FormGroup>
											<div className="flex flex-col w-full rounded-md border border-border">
												<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
													<input
														type="file"
														accept="application/pdf"
														ref={inputRef}
														className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
														multiple
														onChange={handleFileChange}
													/>
													<div className="flex flex-row items-center w-full gap-2">
														<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
															<span className="text-sm">{multipleFiles.length > 0 ? "Add" : "Browse"}</span>
														</div>
														<span className="text-base text-foreground">
															{multipleFiles.length === 1
																? multipleFiles[0].name
																: multipleFiles.length > 1
																	? "More than 2 files selected"
																	: "No file selected"}
														</span>
													</div>
												</div>
												{multipleFiles.length > 0 && (
													<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
														{multipleFiles.map((file, index) => (
															<div key={index} className="flex flex-row justify-between items-center">
																<div className="items-center flex flex-row gap-2">
																	<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																		<IconFile size={18} stroke={2} />
																	</div>
																	<div className="inline-flex flex-col">
																		<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																		<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																	</div>
																</div>

																<div className="inlineflex flex-row w-auto">
																	{/* <Tooltip className="!z-[999]" content="Preview">
                                    <button
                                      className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md"
                                      type="button"
                                      color="green"
                                      onClick={() => previewFile(file)}
                                    >
                                      <IconEye size={18} stroke={2} />
                                    </button>
                                  </Tooltip> */}
																</div>
															</div>
														))}
													</div>
												)}
											</div>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
					<div className="flex justify-end mt-4 gap-4">
						<Button type="button" variant="ghost" onClick={closeModal}>
							Cancel
						</Button>
						<Button type="submit" loading={form.formState.isSubmitting}>
							Submit
						</Button>
					</div>
				</div>
			</form>
		</Form>
	);
}
